import constants from 'utils/Constants';

import { getSubtleCrypto } from './SearchKeyDerivation';

/**
 * Encrypted MiniSearch index persistence layer.
 *
 * Implements the "persist encrypted, never plaintext" half of the search
 * feature's security contract. The main thread calls into this module with
 * JSON strings already serialised from the Web Worker and a `CryptoKey`
 * derived in {@link SearchKeyDerivation}. We handle AES-GCM encryption,
 * IndexedDB IO, and account isolation — the worker itself never touches keys
 * or storage.
 *
 * Account-switching safety
 * ------------------------
 * Records are keyed on a composite string `userId:roomId` and indexed on
 * `userId` for bulk deletion. Two Matrix accounts sharing the same browser
 * session (a logout-then-login flow, or shared-device access) cannot read or
 * overwrite each other's records — even before `clearIndex` runs, the key
 * lookup for user B simply does not hit user A's row, and the ciphertext
 * would be unreadable with a key derived from B's identity.
 *
 * Resilience
 * ----------
 * Every function degrades gracefully when IndexedDB is unavailable (private
 * windows with denied storage, Safari quirks, corrupt profiles):
 * `persistIndex` becomes a no-op, `restoreIndex` returns `null`, `clearIndex`
 * silently swallows the failure. Search still works with an in-memory index
 * for the session, but the user sees a fresh rebuild on every open.
 *
 * @module SearchIndexStore
 */

/**
 * IndexedDB database name for the encrypted MiniSearch snapshots.
 *
 * Re-exported so that `useLogout` can call `indexedDB.deleteDatabase` against
 * this exact name without reaching into `utils/Constants`. That direct-delete
 * bypass is the last-line defence for browsers where
 * `indexedDB.databases()` is unsupported (Firefox < 126, some mobile
 * WebViews) and the generic `clearAllIndexedDB` silently no-ops.
 */
export const SEARCH_INDEX_DB_NAME = constants.SEARCH_INDEX_DB_NAME;
const DB_NAME = SEARCH_INDEX_DB_NAME;
const STORE_NAME = constants.SEARCH_INDEX_STORE_NAME;
const DB_VERSION = 1;
const USER_INDEX_NAME = 'userId';

/** AES-GCM uses a 96-bit IV as its recommended default (RFC 5116 §5.1). */
const AES_GCM_IV_LENGTH_BYTES = 12;

/** Stored alongside records so we can migrate the schema in a future version. */
const SCHEMA_VERSION = 1;

/**
 * The on-the-wire encrypted blob. `iv` + `ciphertext` are both required to
 * decrypt — they are stored side by side in the same IndexedDB record.
 */
export interface EncryptedBlob {
  iv: Uint8Array;
  ciphertext: ArrayBuffer;
}

/**
 * The last-indexed event watermark. Passed in by callers so that on restore
 * we can fetch only events strictly newer than `eventId` / `ts`.
 */
export interface IndexWatermark {
  eventId: string | null;
  ts: number;
}

/**
 * The shape of one IndexedDB record. `id` is the composite `userId:roomId`
 * primary key. `userId` is duplicated as a top-level field so we can drive
 * the secondary `IDBIndex` used by `clearIndex(userId)`.
 */
interface StoredRecord {
  id: string;
  userId: string;
  roomId: string;
  iv: Uint8Array;
  ciphertext: ArrayBuffer;
  watermarkEventId: string | null;
  watermarkTs: number;
  indexedCount: number;
  version: number;
}

const buildCompositeId = (userId: string, roomId: string): string => `${userId}:${roomId}`;

/**
 * Opens (or creates) the search-index IndexedDB database.
 *
 * Returns `null` when IndexedDB is unavailable or the open fails. Callers
 * must treat `null` as "storage disabled for this session".
 */
const openDatabase = (): Promise<IDBDatabase | null> =>
  new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve(null);
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        store.createIndex(USER_INDEX_NAME, 'userId', { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => resolve(null);
    request.onblocked = () => resolve(null);
  });

/**
 * Encrypts a plaintext MiniSearch JSON string with the caller's derived key.
 *
 * A fresh 12-byte IV is generated for every call — reusing an IV under the
 * same key is a well-known AES-GCM forgery risk, so we never derive it from
 * the input.
 */
export const encryptIndex = async (key: CryptoKey, plainJson: string): Promise<EncryptedBlob> => {
  const subtle = getSubtleCrypto();
  if (!subtle) throw new Error('crypto.subtle unavailable');
  const iv = crypto.getRandomValues(new Uint8Array(AES_GCM_IV_LENGTH_BYTES));
  const ciphertext = await subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(plainJson));
  return { iv, ciphertext };
};

/**
 * Decrypts an encrypted blob back to its original MiniSearch JSON string.
 *
 * Throws `DOMException("OperationError")` if the ciphertext has been tampered
 * with or if the key does not match the one used at encryption time. Callers
 * must treat any throw as a corrupt-index case: discard the record and
 * rebuild from scratch. Do not log the exception message — it may leak
 * internal crypto state to monitoring pipelines.
 */
export const decryptIndex = async (key: CryptoKey, blob: EncryptedBlob): Promise<string> => {
  const subtle = getSubtleCrypto();
  if (!subtle) throw new Error('crypto.subtle unavailable');
  const plaintext = await subtle.decrypt({ name: 'AES-GCM', iv: blob.iv }, key, blob.ciphertext);
  return new TextDecoder().decode(plaintext);
};

/**
 * Writes an encrypted MiniSearch snapshot to IndexedDB under the composite
 * `userId:roomId` key.
 *
 * No-ops silently when IndexedDB is unavailable.
 */
export const persistIndex = async (
  userId: string,
  roomId: string,
  plainJson: string,
  key: CryptoKey,
  watermark: IndexWatermark,
  indexedCount: number
): Promise<void> => {
  const db = await openDatabase();
  if (!db) return;
  try {
    const { iv, ciphertext } = await encryptIndex(key, plainJson);
    const record: StoredRecord = {
      id: buildCompositeId(userId, roomId),
      userId,
      roomId,
      iv,
      ciphertext,
      watermarkEventId: watermark.eventId,
      watermarkTs: watermark.ts,
      indexedCount,
      version: SCHEMA_VERSION,
    };
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
      tx.objectStore(STORE_NAME).put(record);
    });
  } finally {
    db.close();
  }
};

/**
 * Reads, decrypts, and returns the MiniSearch snapshot for `userId:roomId`,
 * or `null` if there is no record (a different user, a new room, or cleared
 * storage).
 *
 * On decryption failure (wrong key, tampered ciphertext) the record is
 * **deleted** and `null` is returned — callers should rebuild from scratch.
 */
export const restoreIndex = async (
  userId: string,
  roomId: string,
  key: CryptoKey
): Promise<{ plainJson: string; watermark: IndexWatermark; indexedCount: number } | null> => {
  const db = await openDatabase();
  if (!db) return null;
  try {
    const record = await new Promise<StoredRecord | undefined>((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const req = store.get(buildCompositeId(userId, roomId));
      req.onsuccess = () => resolve(req.result as StoredRecord | undefined);
      req.onerror = () => resolve(undefined);
    });
    if (!record) return null;
    try {
      const plainJson = await decryptIndex(key, { iv: record.iv, ciphertext: record.ciphertext });
      return {
        plainJson,
        watermark: { eventId: record.watermarkEventId, ts: record.watermarkTs },
        indexedCount: record.indexedCount,
      };
    } catch {
      await clearIndex(userId, roomId);
      return null;
    }
  } finally {
    db.close();
  }
};

/**
 * Deletes index records for the given user.
 *
 * - `clearIndex(userId)` — deletes every record for that user (used on logout
 *   and account switch); guaranteed not to touch records belonging to
 *   another `userId`.
 * - `clearIndex(userId, roomId)` — deletes only the single `userId:roomId`
 *   composite record (used on "leave room").
 *
 * Silently no-ops on IndexedDB errors.
 */
export const clearIndex = async (userId: string, roomId?: string): Promise<void> => {
  const db = await openDatabase();
  if (!db) return;
  try {
    await new Promise<void>((resolve) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
      const store = tx.objectStore(STORE_NAME);

      if (roomId) {
        store.delete(buildCompositeId(userId, roomId));
        return;
      }

      const index = store.index(USER_INDEX_NAME);
      const req = index.openCursor(IDBKeyRange.only(userId));
      req.onsuccess = () => {
        const cursor = req.result;
        if (!cursor) return;
        cursor.delete();
        cursor.continue();
      };
    });
  } finally {
    db.close();
  }
};

/**
 * Convenience alias used by `useLogout` so that the logout flow does not need
 * to know the internal naming. Deletes every search index record for a user.
 */
export const clearSearchIndex = (userId: string): Promise<void> => clearIndex(userId);

/**
 * Deletes the entire `chat-s-index` IndexedDB database by name.
 *
 * Unlike `clearIndex` / `clearSearchIndex`, this function never calls
 * `indexedDB.open()` — it only calls `indexedDB.deleteDatabase()`. This
 * avoids the subtle bug where `openDatabase()` recreates an empty DB shell
 * immediately after a successful delete, leaving a visible-but-empty
 * `chat-s-index` entry in DevTools across logout boundaries.
 *
 * Use this in logout flows instead of `clearSearchIndex` for all
 * database-level delete operations.
 */
export const deleteSearchIndexDatabase = (): Promise<void> =>
  new Promise<void>((resolve) => {
    if (typeof indexedDB === 'undefined') {
      resolve();
      return;
    }
    try {
      const req = indexedDB.deleteDatabase(DB_NAME);
      req.onsuccess = () => resolve();
      req.onerror = () => resolve();
      req.onblocked = () => resolve();
    } catch {
      resolve();
    }
  });
